import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  HowToReg as RegisterIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { fetchApiWithoutToken } from '../services/api';
import { generateHMAC } from '../utils/crypto';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Register = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Company registration state
  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  // Employee registration state
  const [companyCode, setCompanyCode] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [employeePassword, setEmployeePassword] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
  };

  const handleCompanyRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 비밀번호 암호화
      const encryptedPassword = await generateHMAC(adminPassword);

      const postData = [
        {
          "컬럼명": "@업체명",
          "값": companyName
        },
        {
          "컬럼명": "@한글명",
          "값": adminName
        },
        {
          "컬럼명": "@아이디",
          "값": adminEmail
        },
        {
          "컬럼명": "@암호",
          "값": encryptedPassword
        },
        {
          "컬럼명": "@종류",
          "값": "업체등록"
        }
      ];

      const response = await fetchApiWithoutToken(
        'DBO.CW_T_회원가입',
        postData
      );

      if (response.status === 200) {
        navigate('/');
      } else {
        setError(response.message || '회사 등록에 실패했습니다.');
      }
    } catch (err) {
      setError('회사 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 비밀번호 암호화
      const encryptedPassword = await generateHMAC(employeePassword);

      const postData = [
        {
          "컬럼명": "@업체명",
          "값": companyCode
        },
        {
          "컬럼명": "@한글명",
          "값": employeeName
        },
        {
          "컬럼명": "@아이디",
          "값": employeeEmail
        },
        {
          "컬럼명": "@암호",
          "값": encryptedPassword
        },
        {
          "컬럼명": "@종류",
          "값": "직원등록"
        }
      ];

      const response = await fetchApiWithoutToken(
        'DBO.CW_T_회원가입',
        postData
      );

      if (response.status === 200) {
        navigate('/');
      } else {
        setError(response.message || '직원 등록에 실패했습니다.');
      }
    } catch (err) {
      setError('직원 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            mt: 2,
            p: 3,
            width: '100%',
            height: '620px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '10px'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2
          }}>
            <RegisterIcon sx={{ color: 'primary.main' }} />
            <Typography component="h1" variant="h5">
              회원가입
            </Typography>
          </Box>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mt: 1,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                py: 2,
              },
              '& .Mui-selected': {
                color: '#FF6B00 !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#FF6B00',
              },
            }}
          >
            <Tab label="업체등록" />
            <Tab label="직원등록" />
          </Tabs>
          <TabPanel value={tabValue} index={0}>
            <Box
              component="form"
              onSubmit={handleCompanyRegister}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                label="업체명(상호명)"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="관리자명"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="아이디"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button 
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mb: 2,mt: 3, color: 'white' }}
                disabled={loading}
                startIcon={<RegisterIcon />}
              >
                {loading ? '등록 중...' : '회원가입'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/')}
                disabled={loading}
                startIcon={<LoginIcon />}
              >
                로그인으로 이동
              </Button>
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Box
              component="form"
              onSubmit={handleEmployeeRegister}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                label="업체코드(8자리)"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                disabled={loading}
                inputProps={{ maxLength: 8 }}
                sx={{ backgroundColor: '#F8F9FA' }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="직원명"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="아이디"
                type="email"
                value={employeeEmail}
                onChange={(e) => setEmployeeEmail(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={employeePassword}
                onChange={(e) => setEmployeePassword(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#F8F9FA' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mb: 2, mt:3, color: 'white' }}
                disabled={loading}
                startIcon={<RegisterIcon />}
              >
                {loading ? '등록 중...' : '회원가입'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/')}
                disabled={loading}
                startIcon={<LoginIcon />}
              >
                로그인으로 이동
              </Button>
            </Box>
          </TabPanel>
          <Box sx={{ mt: 'auto', width: '100%' }}>


          </Box>
        </Paper>
      </Box>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Register;
